/**
 * "This tenant's rent does not match their room" — the system's own flag for
 * a booking whose copy of rent/deposit has drifted from the room it points at
 * (COL-360).
 *
 * The room is the source of truth; the booking is a copy that the profile, the
 * ledger and every reminder read. When they disagree the tenant is being billed
 * for a room they are not in, and nobody should have to notice that by eye.
 * Renders nothing while the two agree. Super admins get the one-click repair,
 * which re-derives the booking and its unpaid invoices from the room and
 * leaves an audit row.
 */
import { TriangleAlert, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useRoom } from "@/hooks/v2/useProperties";
import { useResyncBookingToRoom } from "@/hooks/v2/useReassignBookings";
import { describeResync, detectRoomBaseDrift } from "@/lib/room-base-drift";
import { formatEUR } from "@/lib/v2-format";
import { cn } from "@/lib/utils";

export function RoomBaseDriftNotice({
  bookingId,
  roomId,
  bookingRent,
  bookingDeposit,
  depositLocked,
  canRepair,
  roomTitle,
  className,
}: {
  bookingId: number;
  roomId: number | null | undefined;
  bookingRent: number | null | undefined;
  bookingDeposit: number | null | undefined;
  /** Settled deposit (stage >= key_pickup_pending): never reported as drift. */
  depositLocked: boolean;
  /** Whether to offer the repair — the RPC is admin-gated server-side too. */
  canRepair: boolean;
  roomTitle?: string | null;
  className?: string;
}) {
  const { data: room } = useRoom(roomId ?? null);
  const resync = useResyncBookingToRoom();

  const drift = room
    ? detectRoomBaseDrift({
        bookingRent,
        bookingDeposit,
        roomPrice: (room as { price?: number | null }).price,
        roomDeposit: (room as { deposit_amount?: number | null })
          .deposit_amount,
        depositLocked,
      })
    : null;
  if (!drift) return null;

  const roomLabel =
    roomTitle?.trim() ||
    (room as { title?: string | null } | null)?.title?.trim() ||
    "the room";

  const parts: string[] = [];
  if (drift.rent) {
    parts.push(
      `rent on file ${formatEUR(drift.rent.booking)} vs ${roomLabel}'s ${formatEUR(drift.rent.room)}`
    );
  }
  if (drift.deposit) {
    parts.push(
      `deposit on file ${formatEUR(drift.deposit.booking)} vs ${roomLabel}'s ${formatEUR(drift.deposit.room)}`
    );
  }

  return (
    <div
      role="status"
      className={cn(
        "flex flex-wrap items-center gap-2 rounded-md border border-amber-300/70 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-800/50 dark:bg-amber-500/10 dark:text-amber-200",
        className
      )}
    >
      <TriangleAlert className="h-3.5 w-3.5 shrink-0" />
      <span className="flex-1 min-w-0">
        <span className="font-medium">Doesn't match the room: </span>
        {parts.join("; ")}. The room is the source of truth.
      </span>
      {canRepair && (
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-7 rounded-md border-amber-400/70 bg-white/70 px-2 text-xs text-amber-900 hover:bg-white dark:bg-transparent dark:text-amber-100"
          disabled={resync.isPending}
          onClick={() =>
            resync.mutate(bookingId, {
              // Nothing synced is not "Synced" (same as the Brain card).
              onSuccess: (result) =>
                result.synced
                  ? toast.success("Synced to the room", {
                      description: describeResync(result),
                    })
                  : toast.warning(describeResync(result)),
              onError: (err) =>
                toast.error(
                  err instanceof Error
                    ? err.message
                    : "Could not sync this tenant to the room."
                ),
            })
          }
        >
          <RefreshCw
            className={cn("mr-1 h-3 w-3", resync.isPending && "animate-spin")}
          />
          Sync to room
        </Button>
      )}
    </div>
  );
}
